import React, { useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { LanguageList, ThemeList } from 'store/ducks/globalTypes';
import { globalOp } from 'store/ducks/Global';
import { RootState } from 'store/configureStore';
import { ITitleBarProps } from 'store/ducks/Gaps/types';
import ColorPalettes from 'constants/colorPalettes';
import languagesPage from 'constants/languages';
import i18n from 'constants/translation/i18n';
import CustomSelect from '../CustomSelect';
import * as Styled from './TitleBar.styled';

const { useToken } = theme;

const TitleBar: React.FC<ITitleBarProps> = ({ title, goBackButtons }) => {
  const { token } = useToken();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const colorPalettes = ColorPalettes();
  const { scheme, isSuperAdmin, lang } = useSelector(
    (state: RootState) => state.global
  );
  const titleToken = token as typeof token & {
    cardTriangle?: string;
    cardTriangleShadow?: string;
  };
  // Get buttons from Fragment children
  const buttons =
    goBackButtons &&
    typeof goBackButtons === 'object' &&
    'props' in goBackButtons &&
    goBackButtons.props.children
      ? React.Children.toArray(goBackButtons.props.children)
      : React.Children.toArray(goBackButtons);

  // Check if currently on working area screen
  const isOnWorkingArea = location.pathname === '/working_area';

  // Find working area button or create default one (only if not on working area)
  const workingAreaButton = !isOnWorkingArea
    ? buttons.find(
        (button: any) =>
          button?.props?.text?.toLowerCase().includes('working area') ||
          button?.props?.to === '/working_area'
      ) || (
        <Styled.LogoutBtn
          onClick={() => navigate('/working_area')}
          token={token}
        >
          {t('GO_TO_WORKING_AREA')}
        </Styled.LogoutBtn>
      )
    : null;

  // Get other buttons (not working area button)
  const otherButtons = buttons.filter(
    (button: any) =>
      !(
        button?.props?.text?.toLowerCase().includes('working area') ||
        button?.props?.to === '/working_area'
      )
  );
  const handleThemeChange = (value: ThemeList) => {
    localStorage.setItem('theme', value);
    dispatch(globalOp.setTheme(value));
  };

  const handleLangChange = (value: LanguageList) => {
    localStorage.setItem('lang', value);
    i18n.changeLanguage(value); // Immediately update i18n
    dispatch(globalOp.setLang(value));
  };

  // Ensure i18n is synced with Redux state
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  // Create languages array that updates when language changes
  const languages = useMemo(() => languagesPage(lang), [lang]);

  const handleLogOut = () => {
    Modal.confirm({
      title: t('LOGOUT_CONFIRM_TITLE'),
      icon: <ExclamationCircleOutlined />,
      content: t('LOGOUT_CONFIRM_CONTENT'),
      okText: t('LOGOUT_CONFIRM_OK'),
      cancelText: t('LOGOUT_CONFIRM_CANCEL'),
      onOk: async () => {
        try {
          const isOnSuperAdminPage = location.pathname === '/super_admin';
          const response = isOnSuperAdminPage
            ? await globalOp.superuserLogout()
            : await globalOp.logout();
          sessionStorage.removeItem('user');
          navigate('/');
        } catch (error) {
          console.error('Error during logout:', error);
        }
      }
    });
  };

  return (
    <>
      {/* Desktop navbar */}
      <Styled.StyledNav token={token}>
        <Styled.LogoLink to='/working_area'>
          <Styled.StyledLogo />
        </Styled.LogoLink>
        <Styled.AreaTitle>
          <Styled.TitleContainer
            token={token}
            cardTriangle={titleToken.cardTriangle}
            cardTriangleShadow={titleToken.cardTriangleShadow}
          >
            <div>
              <Styled.SubtitleText>
                {title ? (
                  <Styled.DataEntryTitle>{title}</Styled.DataEntryTitle>
                ) : null}
              </Styled.SubtitleText>
            </div>
          </Styled.TitleContainer>
        </Styled.AreaTitle>
        <Styled.ControlsContainer lang={lang}>
          <Styled.NavigationContainer>
            <Styled.MainButtons>
              <div>{workingAreaButton}</div>
              <div>
                <CustomSelect
                  size='md'
                  options={languages}
                  value={lang}
                  onChange={handleLangChange}
                />
              </div>
              <div className='logout-container'>
                <Styled.LogoutBtn onClick={handleLogOut} token={token} isLogout>
                  {t('LOGOUT')}
                </Styled.LogoutBtn>
              </div>
            </Styled.MainButtons>
            <Styled.MainButtons>
              <div>{otherButtons[0]}</div>
              <div>
                <CustomSelect
                  size='md'
                  options={colorPalettes}
                  value={scheme}
                  onChange={handleThemeChange}
                />
              </div>
              <div></div>
            </Styled.MainButtons>
          </Styled.NavigationContainer>
        </Styled.ControlsContainer>
      </Styled.StyledNav>

      {/* Mobile / Tablet navbar */}
      <Styled.MobileNav token={token}>
        <Styled.MobileNavTop>
          <Styled.LogoLink to='/working_area' style={{ pointerEvents: 'auto' }}>
            <Styled.StyledLogo />
          </Styled.LogoLink>
          {title ? (
            <Styled.MobileNavTitle>
              <Styled.TitleContainer
                token={token}
                cardTriangle={titleToken.cardTriangle}
                cardTriangleShadow={titleToken.cardTriangleShadow}
              >
                <Styled.DataEntryTitle>{title}</Styled.DataEntryTitle>
              </Styled.TitleContainer>
            </Styled.MobileNavTitle>
          ) : null}
          <Styled.LogoutBtn
            className='logout-mobile'
            onClick={handleLogOut}
            token={token}
            isLogout
          >
            {t('LOGOUT')}
          </Styled.LogoutBtn>
        </Styled.MobileNavTop>
        <Styled.MobileNavDropdowns>
          <CustomSelect
            size='md'
            options={languages}
            value={lang}
            onChange={handleLangChange}
          />
          <CustomSelect
            size='md'
            options={colorPalettes}
            value={scheme}
            onChange={handleThemeChange}
          />
          {workingAreaButton && <div>{workingAreaButton}</div>}
          {otherButtons.map((btn, i) => (
            <div key={i}>{btn}</div>
          ))}
        </Styled.MobileNavDropdowns>
      </Styled.MobileNav>
    </>
  );
};

export default TitleBar;
